import React, { useState, useCallback, useEffect } from 'react';
import { collection, addDoc, getDocs, query, where, orderBy, Timestamp } from 'firebase/firestore';
import { db } from '../firebase/config.ts';
import { useAuth } from '../contexts/AuthContext.tsx';
import { Invoice } from '../types/invoice.ts';
import { Contractor } from '../types/contractor.ts';
import { Upload, Download, FileText, Calculator, ArrowRight, CheckCircle } from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { invoiceNumberingService } from '../services/invoiceNumberingService.ts';

interface ProcessedInvoiceData {
  invoiceNumber: string;
  employeeName: string;
  jobName: string;
  jobNumber: string;
  activityCode: string;
  activityDescription: string;
  weekEnding: string;
  hours: number;
  rate: number;
  amount: number;
  payType: string;
  clientName: string;
}

const InvoiceUpload: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [processedData, setProcessedData] = useState<ProcessedInvoiceData[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [excelFile, setExcelFile] = useState<File | null>(null);
  const [excelHeaders, setExcelHeaders] = useState<string[]>([]);
  const [showMapping, setShowMapping] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Contractor selection
  const [contractors, setContractors] = useState<Contractor[]>([]);
  const [selectedContractor, setSelectedContractor] = useState<Contractor | null>(null);
  const [columnMapping, setColumnMapping] = useState({
    employeeCode: '',
    lastName: '',
    firstName: '',
    jobName: '',
    jobNumber: '',
    activityCode: '',
    activityDescription: '',
    payType: '',
    date: '',
    hours: '',
    rate: '',
    amount: ''
  });
  const [autoSplitJobName, setAutoSplitJobName] = useState(false);
  const [columnExamples, setColumnExamples] = useState<any>({});
  const [weekEndingDate, setWeekEndingDate] = useState('');

  function getDefaultWeekEnding(): string {
    const today = new Date();
    
    // Go back 8 days to get to the previous work week's Sunday
    const previousSunday = new Date(today);
    previousSunday.setDate(today.getDate() - 8);
    return previousSunday.toISOString().split('T')[0];
  }

  // Load saved mapping from localStorage
  useEffect(() => {
    const savedMapping = localStorage.getItem('invoiceColumnMapping');
    if (savedMapping) {
      try {
        setColumnMapping(JSON.parse(savedMapping));
      } catch (error) {
        console.error('Error loading saved column mapping:', error);
      }
    }
    
    // Set default week ending date
    setWeekEndingDate(getDefaultWeekEnding());
    
    // Load contractors
    loadContractors();
  }, []);

  const loadContractors = async () => {
    try {
      const contractorsRef = collection(db, 'contractors');
      const q = query(contractorsRef, where('status', '==', 'active'));
      const snapshot = await getDocs(q);
      
      const contractorData = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
          licenseExpires: data.licenseExpires?.toDate(),
          insuranceExpires: data.insuranceExpires?.toDate(),
          bondExpires: data.bondExpires?.toDate(),
        } as Contractor;
      });
      
      setContractors(contractorData);
      
      // Auto-select the first contractor if available
      if (contractorData.length > 0) {
        setSelectedContractor(contractorData[0]);
      }
    } catch (err) {
      console.error('Error loading contractors:', err);
    }
  };

  // Clean currency strings to numeric values
  const cleanCurrency = (value: any) => {
    if (typeof value === 'string') {
      // Remove $ and commas, then parse as float
      const cleaned = value.replace(/[$,]/g, '').trim();
      return cleaned === '' ? 0 : parseFloat(cleaned);
    }
    return typeof value === 'number' ? value : 0;
  };

  // Split combined job name/number field (same logic as TimesheetProcessor)
  const splitJobNameNumber = (combinedValue: string): { jobNumber: string; jobName: string } => {
    if (!combinedValue || typeof combinedValue !== 'string') {
      return { jobNumber: '', jobName: '' };
    }

    console.log('Splitting:', combinedValue);

    // Pattern 1: ##-##### format (like 23-25020)
    const pattern1 = /^(\d{2}-\d{5})\s+(.+)$/;
    const match1 = combinedValue.match(pattern1);
    if (match1) {
      console.log('Pattern 1 matched:', match1[1], '|', match1[2]);
      return {
        jobNumber: match1[1].trim(),
        jobName: match1[2].trim()
      };
    }

    // Pattern 2: CI##-## format (like CI23-52)  
    const pattern2 = /^(CI\d{2}-\d{2})\s+(.+)$/;
    const match2 = combinedValue.match(pattern2);
    if (match2) {
      console.log('Pattern 2 matched:', match2[1], '|', match2[2]);
      return {
        jobNumber: match2[1].trim(),
        jobName: match2[2].trim()
      };
    }

    // Pattern 3: More flexible - any job code followed by space and description
    const pattern3 = /^([A-Z0-9-]{4,12})\s+(.+)$/;
    const match3 = combinedValue.match(pattern3);
    if (match3) {
      console.log('Pattern 3 matched:', match3[1], '|', match3[2]);
      return {
        jobNumber: match3[1].trim(),
        jobName: match3[2].trim()
      };
    }

    console.log('No pattern matched, keeping original:', combinedValue);
    // If no pattern matches, return original as job name with empty job number
    return {
      jobNumber: '',
      jobName: combinedValue.trim()
    };
  };

  // Extract headers from Excel file
  const extractHeaders = useCallback((file: File) => {
    setIsLoading(true);
    setError(null);
    setExcelFile(file);
    
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        
        // Get the range of the sheet
        const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');
        const hasHeaders = range.e.r > 0; // More than one row
        
        // Get column headers
        let headers: string[] = [];
        let examples: any = {};
        
        if (hasHeaders) {
          // Try to read with headers first
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { raw: false });
          
          if (jsonData.length > 0) {
            headers = Object.keys(jsonData[0] as Record<string, any>);
            
            // Extract examples from the first row for each column
            headers.forEach(header => {
              examples[header] = (jsonData[0] as any)[header];
            });
          }
        }
        
        // If no headers were found, generate column letters
        if (headers.length === 0) {
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { 
            raw: false,
            header: 'A'
          });
          
          if (jsonData.length > 0) {
            headers = Object.keys(jsonData[0] as Record<string, any>);
            
            headers.forEach(header => {
              examples[header] = (jsonData[0] as any)[header];
            });
          } else {
            setError('No data found in Excel file');
            setIsLoading(false);
            return;
          }
        }
        
        setExcelHeaders(headers);
        setColumnExamples(examples);
        
        // Auto-apply smart column mapping
        setTimeout(() => {
          const newMapping = autoMapColumns(headers);
          const mappedCount = Object.values(newMapping).filter(Boolean).length;
          if (mappedCount > 0) {
            console.log(`Auto-mapped ${mappedCount} columns successfully`);
          }
        }, 100);
        
        setShowMapping(true);
        setIsLoading(false);
      } catch (err) {
        console.error('Error reading file:', err);
        setError(`Error reading file: ${err}`);
        setIsLoading(false);
      }
    };
    
    reader.onerror = () => {
      setError('Error reading file');
      setIsLoading(false);
    };
    
    reader.readAsArrayBuffer(file);
  }, []);

  // Save mapping to localStorage
  const saveMapping = () => {
    localStorage.setItem('invoiceColumnMapping', JSON.stringify(columnMapping));
  };

  // Smart column mapping function
  const autoMapColumns = (headersToUse: string[] = excelHeaders) => {
    const smartColumnMappings = {
      employeeCode: {
        exact: ['employee code', 'ee code', 'emp code', 'code'],
        contains: ['employee', 'emp', 'ee', 'code'],
        patterns: [/^ee.*code$/i, /employee.*code/i, /emp.*code/i]
      },
      lastName: {
        exact: ['last name', 'lastname', 'last', 'lname', 'l_name', 'surname', 'family name'],
        contains: ['last', 'surname', 'family', 'lname'],
        patterns: [/^l\.?\s*name$/i, /^last/i, /surname$/i]
      },
      firstName: {
        exact: ['first name', 'firstname', 'first', 'fname', 'f_name', 'given name'],
        contains: ['first', 'given', 'fname'],
        patterns: [/^f\.?\s*name$/i, /^first/i]
      },
      jobName: {
        exact: ['job name', 'jobname', 'job', 'project name', 'project', 'job description', 'dist job desc'],
        contains: ['job', 'project', 'dist job'],
        patterns: [/^job.*name$/i, /^project.*name$/i, /dist.*job.*desc/i]
      },
      jobNumber: {
        exact: ['job number', 'jobnumber', 'job #', 'job no', 'project number', 'project #'],
        contains: ['job', 'project'],
        patterns: [/^job.*#?$/i, /^project.*#?$/i, /number$/i]
      },
      activityCode: {
        exact: ['activity code', 'activitycode', 'code', 'dist activity code', 'distribution activity code'],
        contains: ['activity', 'dist activity', 'code'],
        patterns: [/activity.*code/i, /dist.*activity/i]
      },
      activityDescription: {
        exact: ['activity description', 'activitydescription', 'description', 'activity desc', 'dist activity desc'],
        contains: ['activity', 'description', 'desc'],
        patterns: [/activity.*desc/i, /dist.*activity.*desc/i]
      },
      payType: {
        exact: ['pay type', 'paytype', 'type', 'earning desc', 'payment type'],
        contains: ['pay', 'type', 'earning'],
        patterns: [/pay.*type/i, /earning.*desc/i, /^type$/i]
      },
      date: {
        exact: ['date', 'work date', 'day', 'date worked'],
        contains: ['date', 'day'],
        patterns: [/date$/i, /day$/i]
      },
      hours: {
        exact: ['hours', 'hour', 'hrs', 'hr', 'time', 'duration'],
        contains: ['hour', 'hrs'],
        patterns: [/^h\.?r\.?s?$/i, /hours?$/i]
      },
      rate: {
        exact: ['rate', 'hourly rate', 'pay rate', 'unit rate'],
        contains: ['rate'],
        patterns: [/rate$/i]
      },
      amount: {
        exact: ['amount', 'total', 'pay amount', 'earnings'],
        contains: ['amount', 'total', 'earnings'],
        patterns: [/amount$/i, /total$/i, /earnings$/i]
      }
    };
    
    const newMapping = { ...columnMapping };
    
    // Smart matching function with scoring
    const findBestMatch = (field: string, mappings: any) => {
      let bestMatch = '';
      let bestScore = 0;
      
      headersToUse.forEach(header => {
        const headerLower = header.toLowerCase().trim();
        let score = 0;
        
        // Exact match (highest priority)
        if (mappings.exact.includes(headerLower)) {
          score = 100;
        }
        // Contains match (medium priority)
        else if (mappings.contains.some((term: string) => headerLower.includes(term.toLowerCase()))) {
          score = 75;
        }
        // Pattern match (lower priority)
        else if (mappings.patterns.some((pattern: RegExp) => pattern.test(header))) {
          score = 50;
        }
        // Partial word match (lowest priority)
        else {
          const words = headerLower.split(/[\s_-]+/);
          const matchingWords = words.filter(word => 
            mappings.exact.includes(word) || 
            mappings.contains.some((term: string) => word.includes(term.toLowerCase()))
          );
          if (matchingWords.length > 0) {
            score = 25 * matchingWords.length;
          }
        }
        
        // Bonus for exact field name matches
        if (headerLower === field.toLowerCase()) {
          score += 25;
        }
        
        if (score > bestScore && score > 20) { // Minimum threshold
          bestScore = score;
          bestMatch = header;
        }
      });
      
      return bestMatch;
    };
    
    // Auto-map columns with smart matching
    Object.entries(smartColumnMappings).forEach(([field, mappings]) => {
      if (!newMapping[field as keyof typeof columnMapping]) {
        const matchedHeader = findBestMatch(field, mappings);
        if (matchedHeader) {
          (newMapping as any)[field] = matchedHeader;
        }
      }
    });
    
    setColumnMapping(newMapping);
    return newMapping;
  };

  // Process Excel file with mapping
  const processExcelFile = useCallback(async () => {
    if (!excelFile) return;
    
    setIsLoading(true);
    setError(null);
    
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        
        // Determine if we need to use header option
        const hasLetterHeaders = Object.values(columnMapping).some(value => 
          value && /^[A-Z]$/.test(value)
        );
        
        // Read the data with appropriate options
        let jsonData: any[];
        if (hasLetterHeaders) {
          jsonData = XLSX.utils.sheet_to_json(worksheet, { 
            raw: false,
            header: 'A'
          });
        } else {
          jsonData = XLSX.utils.sheet_to_json(worksheet, { raw: false });
        }

        // Process data into required format
        const processedData: ProcessedInvoiceData[] = [];

        // First pass: collect unique jobs for invoice assignment. Group by
        // job number when one exists; when auto-split can't find a job
        // number in the job name, group by the job name text instead so
        // those rows still get their own invoice instead of all collapsing
        // into a single shared fallback invoice (see jobGroupKey below).
        const uniqueJobs = new Set<string>();
        for (const item of jsonData) {
          let jobNumber = item[columnMapping.jobNumber] || '';
          let jobDesc = item[columnMapping.jobName] || '';

          // Auto-split job name/number if enabled and jobNumber is empty but jobDesc has content
          if (autoSplitJobName && jobDesc && (!columnMapping.jobNumber || !jobNumber)) {
            const { jobNumber: extractedNumber } = splitJobNameNumber(jobDesc);
            if (extractedNumber) {
              jobNumber = extractedNumber;
            }
          }

          const jobGroupKey = String(jobNumber).trim() || String(jobDesc).trim();
          if (jobGroupKey) {
            uniqueJobs.add(jobGroupKey);
          }
        }
        
        // Get the starting invoice numbers for all companies
        const startingNumbers: { [prefix: string]: number } = {};
        const companies = ['CEC', 'REL', 'BTC'];
        
        for (const company of companies) {
          try {
            const nextNumber = await invoiceNumberingService.peekNextInvoiceNumber(company);
            startingNumbers[company] = parseInt(company === 'CEC' ? nextNumber : nextNumber.split('-')[1]);
          } catch (error) {
            console.error(`Error getting starting number for ${company}:`, error);
            startingNumbers[company] = 1001;
          }
        }

        // Track how many invoices we've assigned per company
        const invoiceCounters: { [prefix: string]: number } = {
          'CEC': 0,
          'REL': 0,
          'BTC': 0
        };

        // Pre-assign consecutive invoice numbers for all unique jobs
        const jobInvoiceMap: any = {};
        
        // Determine contractor prefix from selected contractor
        let companyPrefix = 'CEC'; // Default
        if (selectedContractor) {
          const contractorName = selectedContractor.companyName.toLowerCase();
          if (contractorName.includes('btc') || contractorName.includes('build.test.connect') || contractorName.includes('build test connect')) {
            companyPrefix = 'BTC';
          } else if (contractorName.includes('reliable') || contractorName.includes('rel')) {
            companyPrefix = 'REL';
          } else if (contractorName.includes('cec') || contractorName.includes('commonwealth')) {
            companyPrefix = 'CEC';
          }
        }
        
        for (const jobNumber of uniqueJobs) {
          let invoiceNumber;
          if (companyPrefix === 'CEC') {
            invoiceNumber = startingNumbers[companyPrefix] + invoiceCounters[companyPrefix];
            jobInvoiceMap[jobNumber] = invoiceNumber.toString();
          } else {
            invoiceNumber = startingNumbers[companyPrefix] + invoiceCounters[companyPrefix];
            jobInvoiceMap[jobNumber] = `${companyPrefix}-${invoiceNumber}`;
          }
          invoiceCounters[companyPrefix]++;
        }
        
        // Second pass: process each row with pre-assigned invoice numbers
        for (const item of jsonData) {
          const firstName = item[columnMapping.firstName] || '';
          const lastName = item[columnMapping.lastName] || '';
          const employeeName = `${firstName} ${lastName}`.trim();
          
          let jobDesc = item[columnMapping.jobName] || '';
          let jobNumber = item[columnMapping.jobNumber] || '';
          
          // Auto-split job name/number if enabled and jobNumber is empty but jobDesc has content
          if (autoSplitJobName && jobDesc && (!columnMapping.jobNumber || !jobNumber)) {
            const { jobNumber: extractedNumber, jobName: extractedName } = splitJobNameNumber(jobDesc);
            if (extractedNumber) {
              jobNumber = extractedNumber;
              jobDesc = extractedName;
            }
          }
          
          const activityCode = item[columnMapping.activityCode] || '';
          const activityDescription = item[columnMapping.activityDescription] || '';
          const payType = item[columnMapping.payType] || 'Regular';
          const hours = parseFloat(item[columnMapping.hours] || 0);
          const rate = cleanCurrency(item[columnMapping.rate] || 0);
          const amount = cleanCurrency(item[columnMapping.amount] || 0);
          const date = item[columnMapping.date] || '';

          // Must match the grouping key used in the first pass above,
          // otherwise rows whose job name has no extractable job number
          // all fall back to the same hardcoded invoice number and get
          // merged with unrelated jobs.
          const jobGroupKey = String(jobNumber).trim() || String(jobDesc).trim();

          const invoiceData: ProcessedInvoiceData = {
            invoiceNumber: jobInvoiceMap[jobGroupKey] || '1001',
            employeeName,
            jobName: jobDesc,
            // If no job number could be extracted, fall back to the job
            // name so the field isn't blank on the invoice/PDF filename.
            jobNumber: jobNumber || jobDesc,
            activityCode,
            activityDescription,
            weekEnding: weekEndingDate,
            hours,
            rate,
            amount,
            payType,
            clientName: selectedContractor?.companyName || ''
          };
          
          processedData.push(invoiceData);
        }
        
        setProcessedData(processedData);
        setShowMapping(false);
        setIsLoading(false);
      } catch (err) {
        console.error('Error processing file:', err);
        setError(`Error processing file: ${err}`);
        setIsLoading(false);
      }
    };
    
    reader.onerror = () => {
      setError('Error reading file');
      setIsLoading(false);
    };
    
    reader.readAsArrayBuffer(excelFile);
  }, [columnMapping, excelFile, selectedContractor, autoSplitJobName, splitJobNameNumber]);

  // Convert to invoices and redirect to ExcelInvoiceGenerator
  const convertToInvoices = () => {
    if (!processedData || !selectedContractor) return;

    // Group timesheet data by job number to create invoices in the same format as TimesheetProcessor
    const jobGroups: { [jobNumber: string]: ProcessedInvoiceData[] } = {};
    processedData.forEach(item => {
      if (!jobGroups[item.jobNumber]) {
        jobGroups[item.jobNumber] = [];
      }
      jobGroups[item.jobNumber].push(item);
    });
    
    // Convert to the format expected by ExcelInvoiceGenerator (same as TimesheetProcessor output)
    const timesheetData = processedData.map(item => ({
      'INV #': item.invoiceNumber,
      'EMPLOYEE': item.employeeName,
      'PAY TYPE': item.payType,
      'Activity Code': item.activityCode,
      'Activity Description': item.activityDescription,
      'JOB NAME': item.jobName,
      'JOB NUMBER': item.jobNumber,
      'WEEK ENDING': item.weekEnding,
      'HOURS': item.hours,
      'BURDENED RATE': item.rate,
      // Raw Amount column value from the source sheet, carried through so
      // ExcelInvoiceGenerator can reconcile it against what actually gets
      // totaled into invoices (catches silent drops from pay-type mismatches,
      // grouping bugs, etc.)
      'SOURCE AMOUNT': item.amount,
    }));

    // Store data in localStorage for ExcelInvoiceGenerator to use (same as TimesheetProcessor)
    const dataWithContractor = {
      timesheetData,
      contractorInfo: selectedContractor
    };
    localStorage.setItem('processedTimesheetData', JSON.stringify(dataWithContractor));
    
    // Navigate to ExcelInvoiceGenerator
    navigate('/invoices/excel-generator');
  };

  const exportToExcel = () => {
    if (!processedData) return;
    
    const exportData = processedData.map(entry => ({
      'Invoice Number': entry.invoiceNumber,
      'Employee': entry.employeeName,
      'Job Name': entry.jobName,
      'Job Number': entry.jobNumber,
      'Activity Code': entry.activityCode,
      'Activity Description': entry.activityDescription,
      'Pay Type': entry.payType,
      'Date': entry.weekEnding,
      'Hours': entry.hours,
      'Rate': entry.rate,
      'Amount': entry.amount,
      'Client': entry.clientName
    }));
    
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(exportData);
    XLSX.utils.book_append_sheet(wb, ws, 'Timesheet Data');
    XLSX.writeFile(wb, 'processed_timesheet_data.xlsx');
  };

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      extractHeaders(acceptedFiles[0]);
    }
  }, [extractHeaders]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls']
    }
  });

  // Render a select field for column mapping
  const renderSelectField = (label: string, field: keyof typeof columnMapping, isRequired = false) => {
    const isSelected = !!columnMapping[field];
    
    return (
      <div className={`space-y-2 bg-white rounded-lg p-4 border ${isSelected ? 'border-green-300 shadow-md' : 'border-gray-200'} transition-all duration-300`}>
        <div className="flex justify-between items-center">
          <label className="block text-sm font-medium text-gray-700 flex items-center">
            {label}
            {isRequired && <span className="text-red-500 ml-1">*</span>}
            {isSelected && (
              <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                <svg className="mr-1 h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                Mapped
              </span>
            )}
          </label>
          {columnExamples[columnMapping[field]] && (
            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-md">
              Example: "{columnExamples[columnMapping[field]]}"
            </span>
          )}
        </div>
        <div className="relative">
          <select 
            value={columnMapping[field]} 
            onChange={(e) => setColumnMapping({...columnMapping, [field]: e.target.value})}
            className={`w-full border ${isSelected ? 'border-green-300 bg-green-50' : 'border-gray-300'} rounded-md shadow-sm p-2 pr-10 appearance-none focus:ring-blue-500 focus:border-blue-500`}
          >
            <option value="">Select column</option>
            {excelHeaders.map(header => (
              <option key={header} value={header}>
                {header} {columnExamples[header] ? `(e.g., "${columnExamples[header]}")` : ''}
              </option>
            ))}
          </select>
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
            <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
              <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/>
            </svg>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-6xl mx-auto p-8">
      {/* File Upload Section */}
      <div className="mb-8 bg-white shadow-xl rounded-xl overflow-hidden">
        <div className="bg-gradient-to-r from-blue-600 to-indigo-700 px-8 py-6 text-white">
          <h1 className="text-3xl font-bold text-center mb-2">Invoice Upload</h1>
          <p className="text-center text-blue-100">
            Upload an Excel file to create invoices with column mapping
          </p>
        </div>
        
        <div className="p-8">
          <div 
            {...getRootProps()} 
            className="border-2 border-dashed border-blue-300 rounded-xl p-10 text-center cursor-pointer transition-all duration-300 hover:border-blue-500 hover:bg-blue-50"
          >
            <input {...getInputProps()} />
            <div className="flex flex-col items-center">
              <Upload className={`h-16 w-16 mb-4 ${isDragActive ? 'text-blue-600' : 'text-blue-400'}`} />
              {isDragActive ? (
                <p className="text-blue-600 text-lg font-medium">Drop the Excel file here...</p>
              ) : (
                <>
                  <p className="text-gray-700 text-lg font-medium mb-2">Drag and drop an invoice Excel file here</p>
                  <p className="text-gray-500">or click to select a file</p>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex justify-center items-center my-8 bg-white p-8 rounded-xl shadow-lg">
          <div className="flex flex-col items-center">
            <div className="animate-spin h-12 w-12 border-2 border-blue-500 border-t-transparent rounded-full mb-4"></div>
            <span className="text-blue-600 text-lg font-medium">Processing file...</span>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="my-8 bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="bg-red-600 px-6 py-4">
            <div className="flex items-center">
              <svg className="h-6 w-6 text-white mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <h3 className="text-lg font-medium text-white">Error Processing File</h3>
            </div>
          </div>
          <div className="px-6 py-4 bg-red-50">
            <p className="text-red-800">{error}</p>
          </div>
        </div>
      )}

      {/* Success State */}
      {success && (
        <div className="my-8 bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="bg-green-600 px-6 py-4">
            <div className="flex items-center">
              <CheckCircle className="h-6 w-6 text-white mr-2" />
              <h3 className="text-lg font-medium text-white">Success!</h3>
            </div>
          </div>
          <div className="px-6 py-4 bg-green-50">
            <p className="text-green-800">{success}</p>
          </div>
        </div>
      )}

      {/* Column Mapping UI */}
      {showMapping && !isLoading && (
        <div className="mb-8 bg-white shadow-xl rounded-xl overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-700 px-8 py-6 text-white">
            <h2 className="text-2xl font-bold text-center mb-2">Map Excel Columns</h2>
            <p className="text-center text-blue-100">
              Match your Excel columns to the required invoice fields
            </p>
          </div>
          
          <div className="p-8">
            {/* Contractor Selection */}
            <div className="mb-6 bg-green-50 rounded-lg p-4 border-l-4 border-green-500">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-green-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <div className="ml-3 w-full">
                  <h3 className="text-lg font-medium text-green-800">Contractor Selection</h3>
                  <p className="text-sm text-green-600 mb-2">
                    Select the contractor for these invoices to apply correct prefixes and client information
                  </p>
                  <select
                    value={selectedContractor?.id || ''}
                    onChange={(e) => {
                      const contractor = contractors.find(c => c.id === e.target.value);
                      setSelectedContractor(contractor || null);
                    }}
                    className="w-full md:w-1/2 border border-gray-300 rounded-md shadow-sm p-2"
                    required
                  >
                    <option value="">Select Contractor</option>
                    {contractors.map((contractor) => (
                      <option key={contractor.id} value={contractor.id}>
                        {contractor.companyName}
                      </option>
                    ))}
                  </select>
                  {selectedContractor && (
                    <div className="mt-2 text-sm text-green-700 bg-green-100 p-2 rounded">
                      <strong>Selected:</strong> {selectedContractor.companyName}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Week Ending Date */}
            <div className="mb-6 bg-blue-50 rounded-lg p-4 border-l-4 border-blue-500">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <div className="ml-3 w-full">
                  <h3 className="text-lg font-medium text-blue-800">Week Ending Date</h3>
                  <p className="text-sm text-blue-600 mb-3">
                    Set the week ending date for all timesheet entries (must be a Sunday)
                  </p>
                  <input
                    type="date"
                    value={weekEndingDate}
                    onChange={(e) => {
                      const dateString = e.target.value;
                      if (dateString) {
                        const [year, month, day] = dateString.split('-').map(Number);
                        const selectedDate = new Date(year, month - 1, day);
                        if (selectedDate.getDay() !== 0) {
                          alert('Week ending date must be a Sunday');
                          return;
                        }
                      }
                      setWeekEndingDate(dateString);
                    }}
                    className="w-48 px-3 py-2 border border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Quick actions */}
            <div className="mb-6 flex flex-wrap gap-2">
              <button 
                onClick={() => {
                  const newMapping = autoMapColumns();
                  const mappedCount = Object.values(newMapping).filter(Boolean).length;
                  if (mappedCount > 0) {
                    alert(`Smart mapping complete! Automatically mapped ${mappedCount} columns.`);
                  } else {
                    alert('No automatic mappings found. Please map columns manually.');
                  }
                }} 
                className="bg-indigo-500 hover:bg-indigo-600 text-white font-medium py-2 px-4 rounded-lg shadow-sm transition-all duration-200 flex items-center justify-center text-sm"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                Smart Auto-Map
              </button>
              
              <button 
                onClick={() => {
                  setColumnMapping({
                    employeeCode: '',
                    lastName: '',
                    firstName: '',
                    jobName: '',
                    jobNumber: '',
                    activityCode: '',
                    activityDescription: '',
                    payType: '',
                    date: '',
                    hours: '',
                    rate: '',
                    amount: ''
                  });
                }} 
                className="bg-gray-500 hover:bg-gray-600 text-white font-medium py-2 px-4 rounded-lg shadow-sm transition-all duration-200 flex items-center justify-center text-sm"
              >
                Reset Mapping
              </button>
            </div>
            
            {/* Required fields section */}
            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-800 mb-3">Required Fields</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                {renderSelectField('First Name', 'firstName', true)}
                {renderSelectField('Last Name', 'lastName', true)}
                {renderSelectField('Job Name', 'jobName', true)}
                {renderSelectField('Hours', 'hours', true)}
                {renderSelectField('Rate', 'rate', true)}
                {renderSelectField('Amount', 'amount', true)}
              </div>
              
              {/* Auto-split Job Name/Number Option */}
              <div className="mb-6 bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-start">
                  <div className="flex items-center h-5">
                    <input
                      id="autoSplitJobName"
                      type="checkbox"
                      checked={autoSplitJobName}
                      onChange={(e) => setAutoSplitJobName(e.target.checked)}
                      className="w-4 h-4 text-amber-600 bg-gray-100 border-gray-300 rounded focus:ring-amber-500 focus:ring-2"
                    />
                  </div>
                  <div className="ml-3 text-sm">
                    <label htmlFor="autoSplitJobName" className="font-medium text-amber-800">
                      Auto-split Combined Job Name/Number Field
                    </label>
                    <p className="text-amber-700 mt-1">
                      Enable this when Job Name contains both job number and name (e.g., "23-25020 LISD Hebron HS 20 Year Refresh"). 
                      The system will automatically extract the job number and separate the job name.
                    </p>
                  </div>
                </div>
              </div>

              
              <h3 className="text-lg font-medium text-gray-800 mb-3">Optional Fields</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                {renderSelectField('Employee Code', 'employeeCode')}
                {renderSelectField('Job Number', 'jobNumber')}
                {renderSelectField('Activity Code', 'activityCode')}
                {renderSelectField('Activity Description', 'activityDescription')}
                {renderSelectField('Pay Type', 'payType')}
                {renderSelectField('Date', 'date')}
              </div>
            </div>
          
            <div className="flex flex-wrap gap-4">
              <button 
                onClick={() => {
                  let requiredFields = ['firstName', 'lastName', 'jobName', 'hours', 'rate', 'amount'];
                  
                  // If auto-split is enabled, jobNumber is not required to be mapped
                  if (autoSplitJobName) {
                    requiredFields = ['firstName', 'lastName', 'jobName', 'hours', 'rate', 'amount'];
                  } else {
                    requiredFields = ['firstName', 'lastName', 'jobName', 'jobNumber', 'hours', 'rate', 'amount'];
                  }
                  
                  const missingFields = requiredFields.filter(field => !columnMapping[field as keyof typeof columnMapping]);
                  
                  if (missingFields.length > 0) {
                    alert(`Please map the following required fields: ${missingFields.join(', ')}`);
                    return;
                  }
                  
                  // Contractor validation
                  if (!selectedContractor) {
                    alert('Please select a contractor before processing the timesheet.');
                    return;
                  }
                  
                  // Additional validation for auto-split
                  if (autoSplitJobName && columnMapping.jobNumber) {
                    alert('When using auto-split, please leave the Job Number field unmapped. The system will extract it from Job Name automatically.');
                    return;
                  }
                  
                  saveMapping();
                  processExcelFile();
                }} 
                className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-6 rounded-lg shadow-md transition-all duration-200 flex items-center justify-center"
              >
                <CheckCircle className="h-5 w-5 mr-2" />
                Process with this Mapping
              </button>
              <button 
                onClick={saveMapping} 
                className="bg-green-600 hover:bg-green-700 text-white font-medium py-3 px-6 rounded-lg shadow-md transition-all duration-200 flex items-center justify-center"
              >
                <Download className="h-5 w-5 mr-2" />
                Save Mapping for Future Use
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Processed Data UI */}
      {processedData && !isLoading && !showMapping && (
        <div className="mb-8 bg-white shadow-xl rounded-xl overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-700 px-8 py-6 text-white">
            <h2 className="text-2xl font-bold text-center mb-2">Processed Timesheet Data</h2>
            <p className="text-center text-blue-100 mt-1">Ready to create invoices grouped by job</p>
          </div>
          
          <div className="p-8">
            <div className="mb-6 bg-green-50 rounded-lg p-4 border-l-4 border-green-500">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-green-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-lg font-medium text-green-800">Invoice Grouping</h3>
                  <p className="text-sm text-green-700 mt-1">
                    Timesheet entries will be grouped by job number to create invoices. 
                    {(() => {
                      const jobGroups = processedData.reduce((groups: any, item) => {
                        if (!groups[item.jobNumber]) groups[item.jobNumber] = [];
                        groups[item.jobNumber].push(item);
                        return groups;
                      }, {});
                      const jobCount = Object.keys(jobGroups).length;
                      return `${jobCount} invoice${jobCount !== 1 ? 's' : ''} will be created from ${processedData.length} timesheet entries.`;
                    })()}
                  </p>
                </div>
              </div>
            </div>

            <div className="mb-6 bg-blue-50 rounded-lg p-4 border-l-4 border-blue-500">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <Calculator className="h-5 w-5 text-blue-600" />
                </div>
                <div className="ml-3">
                  <h3 className="text-lg font-medium text-blue-800">Source Amount Total</h3>
                  <p className="text-sm text-blue-700 mt-1">
                    Sum of the "Amount" column from your uploaded file: {' '}
                    <span className="font-semibold">
                      ${processedData.reduce((sum, item) => sum + (item.amount || 0), 0).toFixed(2)}
                    </span>
                    {' '}across {processedData.length} row(s). Compare this against the invoiced total shown after you click "Convert to Invoices" &mdash; they should match exactly.
                  </p>
                </div>
              </div>
            </div>

            <div className="mb-6 flex flex-wrap gap-4">
              <button 
                onClick={exportToExcel} 
                className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-6 rounded-lg shadow-md transition-all duration-200 flex items-center justify-center"
              >
                <Download className="h-5 w-5 mr-2" />
                Export to Excel
              </button>
              <button 
                onClick={convertToInvoices} 
                className="bg-green-600 hover:bg-green-700 text-white font-medium py-3 px-6 rounded-lg shadow-md transition-all duration-200 flex items-center justify-center"
              >
                <ArrowRight className="h-5 w-5 mr-2" />
                Convert to Invoices
              </button>
              <button 
                onClick={() => setShowMapping(true)} 
                className="bg-yellow-500 hover:bg-yellow-600 text-white font-medium py-3 px-6 rounded-lg shadow-md transition-all duration-200 flex items-center justify-center"
              >
                <FileText className="h-5 w-5 mr-2" />
                Adjust Column Mapping
              </button>
            </div>
            
            <div className="overflow-x-auto bg-white rounded-lg border border-gray-200 shadow-md">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Invoice #</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employee</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Job Name</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Job Number</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Activity</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pay Type</th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Hours</th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Rate</th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {processedData.map((row, index) => (
                    <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{row.invoiceNumber}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{row.employeeName}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{row.jobName}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{row.jobNumber}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {row.activityCode && row.activityDescription 
                          ? `${row.activityCode} - ${row.activityDescription}`
                          : row.activityCode || row.activityDescription || '-'
                        }
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          row.payType === 'Overtime' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'
                        }`}>
                          {row.payType}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">{row.hours}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">${row.rate.toFixed(2)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">${row.amount.toFixed(2)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{row.weekEnding}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoiceUpload;